// =============================================================================
// iTravel 2.0 — FullScreenLoader Component
// =============================================================================
// A centered, full-screen loading indicator used as a Suspense fallback
// for lazy-loaded route components and during initial app hydration.
// =============================================================================

import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';
import { alpha, useTheme } from '@mui/material/styles';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface FullScreenLoaderProps {
  /** Optional message to display below the spinner */
  message?: string;
  /** Whether to show a translucent overlay (for loading on top of content) */
  overlay?: boolean;
  /** Size of the spinner in pixels */
  size?: number;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function FullScreenLoader({ message, overlay = false, size = 40 }: FullScreenLoaderProps) {
  const theme = useTheme();

  return (
    <Box
      role="status"
      aria-label={message ?? 'Loading'}
      aria-busy="true"
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: overlay ? undefined : '100dvh',
        width: '100%',
        height: overlay ? '100%' : undefined,
        gap: 2,
        // Overlay mode: cover parent with a translucent backdrop
        ...(overlay && {
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: theme.zIndex.modal - 1,
          backgroundColor: alpha(theme.palette.background.default, 0.75),
          backdropFilter: 'blur(4px)',
        }),
        // Non-overlay mode: subtle background
        ...(!overlay && {
          backgroundColor: theme.palette.background.default,
        }),
      }}
    >
      <CircularProgress
        size={size}
        thickness={3.5}
        sx={{
          color: theme.palette.primary.main,
          // Subtle drop shadow to make the spinner pop against any bg
          filter: `drop-shadow(0 2px 8px ${alpha(theme.palette.primary.main, 0.25)})`,
        }}
      />

      {message && (
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{
            fontWeight: 500,
            textAlign: 'center',
            maxWidth: 280,
            px: 2,
            // Prevent layout shift by reserving a line height
            lineHeight: 1.5,
            // Subtle fade-in animation
            animation: 'fadeIn 0.3s ease-in',
            '@keyframes fadeIn': {
              from: { opacity: 0, transform: 'translateY(4px)' },
              to: { opacity: 1, transform: 'translateY(0)' },
            },
          }}
        >
          {message}
        </Typography>
      )}
    </Box>
  );
}

export default FullScreenLoader;
